import React, { useState, useEffect, useRef } from 'react';
import { useStateMachine } from 'little-state-machine';
import { setChatHistory } from './chatbotAction.ts';
import MarkdownIt from 'markdown-it';
import MarkdownItExternalLinks from 'markdown-it-external-links';
import { userAppContext } from '../../Context/Context';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

export default function ChatBot({
  chatId = 'default',
  message = '',
  question = '',
  shortQuestion = '',
  autoSend = false,
  style = {},
}) {
  const { isAuthenticated, userApiKey, groups } = userAppContext();
  const { actions, state } = useStateMachine({ actions: { setChatHistory } });
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [downloadId, setDownloadId] = useState(null);
  const [lastActivityTime, setLastActivityTime] = useState(new Date().getTime());
  const [inputMessage, setInputMessage] = useState('');
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isBotResponding, setIsBotResponding] = useState(false);
  const [isChatActive, setIsChatActive] = useState(false);
  const [hasAutoSent, setHasAutoSent] = useState(false);

  const localStorageKey = `chatMessages-${chatId}`;

  const initialBotMessage = {
    id: 'bot',
    sender: 'bot',
    message:
      message !== ''
        ? message
        : 'Hello! How can I help you?\nDo you want to see what kind of questions I can help you answer?',
    timestamp: new Date(),
  };

  // if no previous history, set initial message
  useEffect(() => {
    if (state?.chatHistory && state?.chatHistory.length === 0) {
      actions.setChatHistory([initialBotMessage]);
    }
  }, []);

  const [messages, setMessages] = useState(() => {
    const savedMessages = localStorage.getItem(localStorageKey);
    return savedMessages
      ? JSON.parse(savedMessages).map(message => ({ ...message, timestamp: new Date(message.timestamp) }))
      : [initialBotMessage];
  });

  const messagesEndRef = useRef(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // scroll timer
  useEffect(() => {
    scrollToBottom();
    localStorage.setItem(localStorageKey, JSON.stringify(messages));
  }, [messages, localStorageKey]);

  const resetInactivityTimeout = () => {
    setLastActivityTime(new Date().getTime());
  };

  // inactivity timer
  useEffect(() => {
    const checkInactivity = setInterval(() => {
      const currentTime = new Date().getTime();
      if (currentTime - lastActivityTime >= 3600000) {
        handleEndChat(null, userApiKey);
      }
    }, 600000);

    return () => clearInterval(checkInactivity);
  }, [lastActivityTime, userApiKey]);

  const stopTyping = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsBotResponding(false);
    setIsBotTyping(false);
  };

  const handleSendMessage = async (e, userApiKey) => {
    const messageToSend = (question || inputMessage).trim();

    if (messageToSend === '') return;

    const currentTime = new Date();
    const userMessage = {
      id: `user-${currentTime.getTime()}`,
      sender: 'user',
      message: messageToSend,
      timestamp: currentTime,
    };

    setMessages(prevMessages => [...prevMessages, userMessage]);
    actions.setChatHistory(prevChatHistory => [...prevChatHistory, userMessage]);
    setInputMessage('');
    setIsBotTyping(true);
    resetInactivityTimeout();
    setIsChatActive(true);
    scrollToBottom();
    await handleReceiveMessage(userMessage.message, userApiKey);
  };

  const abortControllerRef = useRef(null);

  const handleReceiveMessage = async (userMessage, userApiKey) => {
    const currentTime = new Date();
    const url = `${process.env.REACT_APP_URL_CHAT_API}get?msg=${encodeURIComponent(userMessage)}`;
    const headers = { Authorization: userApiKey };

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsBotTyping(true);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: abortController.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error('Network response was not ok');
      }

      let botMessageId = `bot-${currentTime.getTime()}`;
      let botMessageText = '';

      setIsBotTyping(true);
      setIsBotResponding(true);

      setMessages(prevMessages => [
        ...prevMessages,
        { id: botMessageId, sender: 'bot', message: '', timestamp: new Date() },
      ]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      actions.setChatHistory(prev => [
        ...prev,
        { id: botMessageId, sender: 'bot', message: '', timestamp: new Date() },
      ]);

      // Read the stream
      let done = false;

      while (!done) {
        if (abortController.signal.aborted) {
          console.log('⛔ Stream aborted manually before read.');
          break;
        }

        const { value, done: chunkDone } = await reader.read();
        done = chunkDone;

        if (abortController.signal.aborted) {
          console.log('⛔ Stream aborted manually after read.');
          break;
        }

        if (value) {
          let chunk = decoder.decode(value, { stream: true });

          setIsBotTyping(false);

          for (let char of chunk) {
            if (abortController.signal.aborted) {
              console.log('⛔ Aborted during chunk typing');
              break;
            }
            botMessageText += char;

            setMessages(prevMessages => {
              const updatedMessages = [...prevMessages];
              const lastIndex = updatedMessages.findIndex(msg => msg.id === botMessageId);
              if (lastIndex !== -1) {
                updatedMessages[lastIndex] = { ...updatedMessages[lastIndex], message: botMessageText };
              }
              return updatedMessages;
            });

            await new Promise(resolve => setTimeout(resolve, 1));
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('⛔ Bot response aborted');
      } else {
        console.error(error);
        actions.setChatHistory(prev => [
          ...prev,
          {
            id: `bot-error-${currentTime.getTime()}`,
            sender: 'bot',
            message: 'Sorry, there was an error.',
            timestamp: new Date(),
          },
        ]);
      }
    } finally {
      setIsBotTyping(false);
      setIsBotResponding(false);
      abortControllerRef.current = null;
    }
  };

  const handleEndChat = async (e, userApiKey) => {
    const headers = { Authorization: userApiKey };
    const urlClear = `${process.env.REACT_APP_URL_CHAT_API}clear`;

    try {
      const response = await fetch(urlClear, { method: 'GET', headers: headers, credentials: 'include' });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      actions.setChatHistory([initialBotMessage]);
      localStorage.removeItem(localStorageKey);
      setMessages([initialBotMessage]);
      setIsChatActive(false);
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        setInputMessage(inputMessage + '\n');
      } else {
        handleSendMessage(e, userApiKey);
      }
      e.preventDefault();
    }
  };

  const handleButtonClick = e => {
    if (isBotTyping || isBotResponding) {
      stopTyping();
    } else {
      handleSendMessage(e, userApiKey);
      setInputMessage('');
    }
  };

  const formatTimestamp = timestamp => {
    // Convert timestamp to a Date object if it's a string
    const date = new Date(timestamp);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid timestamp:', timestamp);
      return 'Invalid Date';
    }

    const options = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' };
    return new Intl.DateTimeFormat('es-ES', options).format(date);
  };

  const formatMessage = message => {
    const md = new MarkdownIt();
    md.use(MarkdownItExternalLinks, { externalClassName: 'external-link' });

    md.renderer.rules.link_open = (tokens, idx) => {
      const href = tokens[idx].attrGet('href');
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">`;
    };

    md.renderer.rules.link_close = (tokens, idx) => {
      return ' <i class="bi bi-box-arrow-up-right"></i>&nbsp;</a>';
    };

    return md.render(message);
  };

  const copyToClipboard = (message, messageId) => {
    navigator.clipboard.writeText(message).then(
      () => {
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 1000);
      },
      err => {
        console.error('Error copying text to clipboard:', err);
      },
    );
  };

  const downloadChatAsTxt = () => {
    const textContent = messages
      ?.map(msg => {
        const time = formatTimestamp(msg.timestamp);
        return `[${time}] ${msg.sender === 'user' ? 'You' : 'Bot'}: ${msg.message}`;
      })
      ?.join('\n');

    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'chat.txt';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);

    setDownloadId(true);

    setTimeout(() => {
      setDownloadId(null);
    }, 2000);
  };

  const isAcademicUser = groups?.includes('ROLE_ACADEMIC');

  useEffect(() => {
    if (shortQuestion) {
      setInputMessage(shortQuestion);
    } else if (question) {
      setInputMessage(question);
    }
  }, [question, shortQuestion]);

  useEffect(() => {
    if (autoSend && question && !hasAutoSent && isAuthenticated && userApiKey) {
      setHasAutoSent(true);

      setTimeout(() => {
        handleSendMessage(null, userApiKey);
      }, 500);
    }
  }, [autoSend, question, hasAutoSent, isAuthenticated, userApiKey]);

  return isAuthenticated && !isAcademicUser ? (
    <React.Fragment>
      <div className="pt-2 container overflow-bot">
        <div>
          <h1 className="text-center p-2">
            DISGENET Assistant <i className="bi bi-stars"></i>
          </h1>
          <div className="text-center px-3 pb-2">
            <small className="text-muted">
              This assistant uses OpenAI technology and Retrieval-Augmented Generation (RAG) to provide
              responses based on DISGENET data. Outputs may contain errors or hallucinations and should be
              independently verified. For more information{' '}
              <a
                href="/documents/medbio_chatbot_disclaimer.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-underline"
              >
                click here
              </a>
              .
            </small>
          </div>
        </div>
        <div className="p-2 row justify-content-center">
          <div className="chat-bot-wrapper">
            <div className="chat-bot-container">
              <div className="chat-bot-messages">
                {messages?.map((message, index) => (
                  <React.Fragment key={`message-${index}`}>
                    {message.sender === 'user' && (
                      <div>
                        <div className="timestamp-message">{formatTimestamp(message.timestamp)}</div>
                        <div className="user-message d-flex justify-content-end">
                          <div
                            className="bot-message-content"
                            dangerouslySetInnerHTML={{ __html: formatMessage(message.message) }}
                          />
                        </div>
                      </div>
                    )}
                    {message.sender === 'bot' && (
                      <div>
                        <div className="timestamp-message ">{formatTimestamp(message.timestamp)}</div>
                        <div className="bot-message received d-flex justify-content-start">
                          <div className="bot-avatar">
                            <i className="bi bi-robot"></i>
                          </div>
                          <div className="bot-message-content position-relative">
                            <div dangerouslySetInnerHTML={{ __html: formatMessage(message.message) }} />

                            <br></br>
                            {message.message !== initialBotMessage.message && message.isValid && (
                              <button
                                className="copy-bot-btn"
                                onClick={() => copyToClipboard(message.message, message.id)}
                              >
                                {copiedMessageId === message.id ? (
                                  <span>
                                    Copied <i className="bi-check"></i>
                                  </span>
                                ) : (
                                  <span>
                                    Copy <i className="bi-copy"></i>
                                  </span>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
                <div ref={messagesEndRef} />
                {isBotTyping && (
                  <div className="bot-message d-flex justify-content-start">
                    <div className="bot-avatar">
                      <i className="bi bi-robot"></i>
                    </div>
                    <div className="bot-message-content">
                      <i>typing...</i>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="div-chat-bot-input mt-auto p-2 d-flex">
            <div className="chat-input-container">
              <textarea
                className="chat-bot-input"
                placeholder="Type your question..."
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                style={style}
              />
              <button className="button-chat-bot" aria-label="Send message" onClick={handleButtonClick}>
                <i className="bi bi-send"></i>
              </button>
            </div>
          </div>
          <div className="chat-actions">
            <div className="end-chat">
              <button className=" btn-gradient-pink" onClick={e => handleEndChat(e, userApiKey)}>
                Clear Chat
              </button>
            </div>
            <div className="download-chat">
              <button className=" btn-gradient-pink" onClick={downloadChatAsTxt}>
                {downloadId ? (
                  <span>
                    Download <i className="bi-check"></i>
                  </span>
                ) : (
                  <span>Download Chat</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <div className="container overflow-bot">
        <div>
          <h1 className="text-center p-2">
            DISGENET Assistant <i className="bi bi-stars"></i>
          </h1>
        </div>
        <div className="p-2 row justify-content-center">
          <div className="chat-bot-wrapper">
            <div className="chat-bot-container">
              <div className="chat-bot-messages">
                <React.Fragment>
                  {isAcademicUser ? (
                    <div className="bot-message received d-flex justify-content-start">
                      <div className="bot-avatar">
                        <i className="bi bi-robot"></i>
                      </div>
                      <p className="bot-message-content">
                        As an academic user, you do not have access to the chat. Please explore other options
                        available in{' '}
                        <a className="" href="/plans" target="_blank" rel="noopener noreferrer">
                          <u>Plans</u>.
                        </a>{' '}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="bot-message received d-flex justify-content-start">
                        <div className="bot-avatar">
                          <i className="bi bi-robot"></i>
                        </div>
                        <p className="bot-message-content">
                          Hello! You need to be logged in to access the chat
                        </p>
                      </div>
                      <div className="bot-message received d-flex justify-content-start">
                        <div className="bot-avatar">
                          <i className="bi bi-robot"></i>
                        </div>
                        <p className="bot-message-content">
                          Access:{' '}
                          <a className="" href="/login" target="_blank" rel="noopener noreferrer">
                            <u>Login</u>
                          </a>
                          ,{' '}
                          <a className="" href="/plans" target="_blank" rel="noopener noreferrer">
                            <u>Register</u>
                          </a>{' '}
                          or{' '}
                          <a className="" href="/Support" target="_blank" rel="noopener noreferrer">
                            <u>SUPPORT</u>

                            <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                          </a>
                        </p>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
